import { useMemo, useRef, useState } from 'react';
import {
  KIND_LABEL, CAPTURE_LABEL, compareFrames, defaultPair, clampSplit, humanDate, vsLine, headerLine, visitStatus, latestViewable, galleryItems,
} from './siteTimelineModel';
import MeasureView from './MeasureView';
import Model3DView from './Model3DView';
import ScanView from './ScanView';
import ProgressNote from './ProgressNote';
import SiteGallery from './SiteGallery';
import SiteReport from './SiteReport';
import { canReport } from './siteReportModel';
import { viewFrames } from './site3dModel';
import { changeBlocker } from './site3dChange';
import ChangeSummary from './ChangeSummary';
import { theme, Icon, Button, Chip, StatusPill, Segmented, DatePicker, KIND_ICON, STAGE, F } from './siteUi';
import { useIsPhone, useStageSize, FrameMarks, GRID } from './SiteStage';

const CSS = `
.sp-row{display:grid;grid-template-columns:minmax(150px,200px) minmax(0,1fr) auto;gap:12px 32px;align-items:start}
.sp-row>.sp-body{min-width:0}
.sp-row>.sp-full{grid-column:1 / -1;min-width:0}
@media (max-width:719px){.sp-row{grid-template-columns:minmax(0,1fr) auto;gap:16px 16px}.sp-row>.sp-body{grid-column:1 / -1;grid-row:2}}
.sp-chip:hover:not(:disabled){border-color:currentColor}
.sp-bleed{margin-left:0;margin-right:0}
@media (max-width:719px){.sp-bleed{margin-left:-20px;margin-right:-20px}}
`;

const viewWord = (kind) => (kind === 'site_scan' ? 'View scan' : 'View 3D');
const itemOf = (d, v) => ({ url: d.url, format: d.view.format, kind: d.kind, date: v.date, label: d.label, id: d.id || d.url });

function SectionHead({ t, title, sub, right, testid }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
      <div style={{ minWidth: 0 }}>
        <h2 style={{ margin: 0, fontFamily: F.display, fontSize: 22, fontWeight: 500, letterSpacing: '-0.01em', color: t.ink }}>{title}</h2>
        {sub ? <p data-testid={testid} style={{ margin: '4px 0 0', fontSize: 14, color: t.mute }}>{sub}</p> : null}
      </div>
      {right}
    </div>
  );
}

function IdleStage({ item, canCompare, onOpen, onCompare }) {
  const box = useRef(null);
  const size = useStageSize(box);
  return (
    <div ref={box} data-testid="site-stage-idle" style={{ position: 'relative', height: size.height, minHeight: size.minHeight, background: STAGE.bg, borderRadius: 2, overflow: 'hidden',
      backgroundImage: GRID,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, boxSizing: 'border-box' }}>
      <FrameMarks />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 20, color: STAGE.ink, maxWidth: 420 }}>
        <Icon name={KIND_ICON[item.kind] || 'model'} size={40} color={STAGE.ink} />
        <div>
          <div style={{ fontSize: 20, fontWeight: 500 }}>{KIND_LABEL[item.kind] || '3D view'}</div>
          <div style={{ fontSize: 14, color: STAGE.mute, marginTop: 4 }}>{humanDate(item.date)}</div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
          <Button tone="dark" onClick={onOpen} icon={KIND_ICON[item.kind] || 'model'}>{item.kind === 'site_scan' ? 'Open scan' : 'Open in 3D'}</Button>
          {canCompare ? <Button tone="dark" variant="secondary" icon="compare" onClick={onCompare}>Compare dates</Button> : null}
        </div>
      </div>
    </div>
  );
}

// The one 3D stage on the page: a single date, or two dates with one camera.
function SiteViewer({ t, tone, visits, kinds3d, view, setView, stageRef }) {
  const latest = useMemo(() => latestViewable(visits), [visits]);
  const [kind, setKind] = useState(kinds3d[0]);
  const activeKind = kinds3d.includes(kind) ? kind : kinds3d[0];
  const frames = useMemo(() => (activeKind ? viewFrames(visits, activeKind) : []), [visits, activeKind]);
  const [pick, setPick] = useState([null, null]);
  const [look, setLook] = useState('side');
  const [change, setChange] = useState(null);
  const [leftId, rightId] = defaultPair(frames, pick[0], pick[1]);
  const left = frames.find((f) => f.visitId === leftId);
  const right = frames.find((f) => f.visitId === rightId);
  const canCompare = Boolean(kinds3d.length && left && right);
  if (!latest) return null;

  const mode = view && view.mode === 'compare' && canCompare ? 'compare' : view && view.mode === 'single' ? 'single' : 'idle';
  const shown = mode === 'single' ? view.item : latest;
  const label = (k) => KIND_LABEL[k] || '3D view';
  const title = mode === 'compare' ? `Compare ${label(activeKind).toLowerCase()}s` : label(shown.kind);
  const sub = mode === 'compare' ? vsLine(left.date, right.date) : humanDate(shown.date);
  const opts = (other) => frames.map((f) => ({ value: f.visitId, label: humanDate(f.date), disabled: f.visitId === other }));
  const ordered = mode === 'compare' ? (left.date <= right.date ? [left, right] : [right, left]) : null;
  const canChange = mode === 'compare' && activeKind === 'point_cloud' && !changeBlocker(ordered[0], ordered[1]);
  const showChange = canChange && look === 'changes';
  const pickPair = (next) => { setPick(next); setChange(null); };

  return (
    <section data-testid="site-viewer" data-report-surface={mode === 'compare' ? '' : undefined} style={{ marginBottom: 56 }}>
      <SectionHead t={t} title={title} sub={sub} testid="site-viewer-sub"
        right={canCompare ? (
          <Segmented tone={tone} label="Viewer" value={mode === 'compare' ? 'compare' : 'single'}
            onChange={(m) => setView(m === 'compare' ? { mode: 'compare' } : { mode: 'single', item: shown })}
            options={[{ value: 'single', label: 'Single date' }, { value: 'compare', label: 'Compare dates' }]} />
        ) : null} />
      {mode === 'compare' ? (
        <div data-testid="site-compare-3d" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          {kinds3d.length > 1 ? (
            <Segmented tone={tone} label="3D deliverable to compare" value={activeKind}
              onChange={(k) => { setKind(k); pickPair([null, null]); }} options={kinds3d.map((k) => ({ value: k, label: label(k) }))} />
          ) : null}
          <DatePicker tone={tone} label="Earlier date" value={leftId} options={opts(rightId)} onChange={(id) => pickPair([id, rightId])} />
          <span style={{ fontSize: 14, color: t.mute }}>vs</span>
          <DatePicker tone={tone} label="Later date" value={rightId} options={opts(leftId)} onChange={(id) => pickPair([leftId, id])} />
          {canChange ? (
            <Segmented tone={tone} label="How to compare" value={look} onChange={(v) => { setLook(v); setChange(null); }}
              options={[{ value: 'side', label: 'Side by side' }, { value: 'changes', label: 'Changes' }]} />
          ) : null}
        </div>
      ) : null}
      <div ref={stageRef} className="sp-bleed" style={{ scrollMarginTop: 24 }}>
        {mode === 'idle' ? (
          <IdleStage item={latest} canCompare={canCompare} onOpen={() => setView({ mode: 'single', item: latest })} onCompare={() => setView({ mode: 'compare' })} />
        ) : null}
        {mode === 'single' ? (
          shown.kind === 'site_scan'
            ? <ScanView key={shown.url} items={[shown]} title={`${label(shown.kind)}, ${humanDate(shown.date)}`} onClose={() => setView(null)} />
            : <Model3DView key={shown.url} items={[shown]} title={`${label(shown.kind)}, ${humanDate(shown.date)}`} onClose={() => setView(null)} />
        ) : null}
        {mode === 'compare' ? (
          activeKind === 'site_scan'
            ? <ScanView key={`${ordered[0].url}|${ordered[1].url}`} items={ordered} title={`${title}, ${sub}`} onClose={() => setView(null)} />
            : <Model3DView key={`${showChange ? 'changes:' : ''}${ordered[0].url}|${ordered[1].url}`} items={ordered} mode={showChange ? 'changes' : 'side'}
                onChanges={setChange} title={`${showChange ? 'Changes' : title}, ${sub}`} onClose={() => setView(null)} />
        ) : null}
      </div>
      {showChange ? <ChangeSummary t={t} result={change} /> : null}
    </section>
  );
}

// Map/picture compare: one frame, a swipe line between two dates.
function ImageCompare({ t, tone, visits, kinds }) {
  const [kind, setKind] = useState(kinds[0]);
  const activeKind = kinds.includes(kind) ? kind : kinds[0];
  const frames = useMemo(() => compareFrames(visits, activeKind), [visits, activeKind]);
  const [pick, setPick] = useState([null, null]);
  const [look, setLook] = useState('side');
  const [change, setChange] = useState(null);
  const [leftId, rightId] = defaultPair(frames, pick[0], pick[1]);
  const left = frames.find((f) => f.visitId === leftId);
  const right = frames.find((f) => f.visitId === rightId);
  const [split, setSplit] = useState(50);
  if (!left || !right) return null;
  const opts = (other) => frames.map((f) => ({ value: f.visitId, label: humanDate(f.date), disabled: f.visitId === other }));
  const name = KIND_LABEL[activeKind] || 'File';
  const tag = { position: 'absolute', top: 20, padding: '6px 10px', fontSize: 13, fontWeight: 500, background: STAGE.panel, color: STAGE.ink, border: `1px solid ${STAGE.hair}`, borderRadius: 2, pointerEvents: 'none' };

  return (
    <section data-testid="site-compare" style={{ marginBottom: 56 }}>
      <SectionHead t={t} title={`Compare ${name.toLowerCase()}s`} sub={vsLine(left.date, right.date)} />
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        {kinds.length > 1 ? (
          <Segmented tone={tone} label="Deliverable to compare" value={activeKind} onChange={(k) => { setKind(k); setPick([null, null]); }}
            options={kinds.map((k) => ({ value: k, label: KIND_LABEL[k] || k }))} />
        ) : null}
        <DatePicker tone={tone} label={`Earlier ${name.toLowerCase()} date`} value={leftId} options={opts(rightId)} onChange={(id) => setPick([id, rightId])} />
        <span style={{ fontSize: 14, color: t.mute }}>vs</span>
        <DatePicker tone={tone} label={`Later ${name.toLowerCase()} date`} value={rightId} options={opts(leftId)} onChange={(id) => setPick([leftId, id])} />
      </div>
      <div className="sp-bleed" style={{ position: 'relative', height: 'clamp(320px, 62vh, 720px)', background: STAGE.bg, overflow: 'hidden', borderRadius: 2 }}>
        <img src={right.src} alt={`${name}, ${humanDate(right.date)}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
        <img src={left.src} alt={`${name}, ${humanDate(left.date)}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', clipPath: `inset(0 ${100 - split}% 0 0)` }} />
        <div aria-hidden style={{ position: 'absolute', top: 0, bottom: 0, left: `${split}%`, width: 1, background: STAGE.ink }}>
          <span style={{ position: 'absolute', top: '50%', left: -10, width: 20, height: 20, marginTop: -10, background: STAGE.bg, border: `1px solid ${STAGE.ink}`, boxSizing: 'border-box' }} />
        </div>
        <span style={{ ...tag, left: 20 }}>{humanDate(left.date)}</span>
        <span style={{ ...tag, right: 20 }}>{humanDate(right.date)}</span>
        <input type="range" min={0} max={100} value={split} onChange={(e) => setSplit(clampSplit(e.target.value))} aria-label="Swipe between dates"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', margin: 0, opacity: 0, cursor: 'ew-resize' }} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
        <Button tone={tone} variant="tertiary" compact icon="download" href={left.url}>{humanDate(left.date)}</Button>
        <Button tone={tone} variant="tertiary" compact icon="download" href={right.url}>{humanDate(right.date)}</Button>
      </div>
    </section>
  );
}

function ReportChip({ tone, v, onReport }) {
  return (
    <Chip tone={tone} icon="report" label="Site report" data-action="report" className="sp-chip"
      aria-label={`Site report, ${humanDate(v.date) || 'visit'}`} onClick={() => onReport(v.id)} />
  );
}

function DeliverableChips({ t, tone, v, view, onView, measuring, setMeasuring, media, gallery, onGallery, onReport }) {
  const queued = v.deliverables.filter((d) => d.scan && d.scan.status === 'queued');
  const inGallery = new Map(media.map((m, i) => [m.id, i]));
  const photos = media.filter((m) => m.type === 'image').length;
  let photosShown = false;
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {v.deliverables.map((d, i) => {
          const key = d.id || `${v.id}-${i}`;
          const icon = KIND_ICON[d.kind] || 'file';
          if (inGallery.has(key)) {
            const at = inGallery.get(key);
            if (media[at].type === 'video') {
              return (
                <Chip key={key} tone={tone} icon="video" label={d.label} trailing="play" data-action="play" className="sp-chip"
                  aria-label={`Play ${d.label}, ${humanDate(v.date)}`} onClick={() => onGallery({ start: at, n: Date.now() })} />
              );
            }
            if (photosShown) return null;
            photosShown = true;
            return (
              <Chip key={key} tone={tone} icon="photos" label="Photos" note={String(photos)} active={!!gallery} data-action="gallery" className="sp-chip"
                aria-label={`Photos, ${humanDate(v.date)}, ${photos}`} aria-expanded={!!gallery} onClick={() => onGallery(gallery ? null : { start: null, n: Date.now() })} />
            );
          }
          if (d.scan && d.scan.status === 'queued') {
            return <Chip key={key} tone={tone} icon="video" label={d.label} note="Queued" disabled data-action="queued" className="sp-chip" />;
          }
          if (d.view) {
            const id = d.id || d.url;
            const on = view && view.mode === 'single' && view.item.id === id;
            return (
              <Chip key={key} tone={tone} icon={icon} label={d.label} active={on} data-action="view" className="sp-chip"
                aria-label={`${viewWord(d.kind)}: ${d.label}, ${humanDate(v.date)}`} onClick={() => onView(itemOf(d, v))} />
            );
          }
          if (d.measure && d.id) {
            return (
              <Chip key={key} tone={tone} icon={icon} label={d.label} trailing="measure" active={measuring === d.id} data-action="measure" className="sp-chip"
                aria-label={`Open and measure: ${d.label}`} onClick={() => setMeasuring(measuring === d.id ? null : d.id)} />
            );
          }
          return <Chip key={key} tone={tone} icon={icon} label={d.label} trailing="open" href={d.url} data-action="open" className="sp-chip" aria-label={`Open ${d.label}`} />;
        })}
        {onReport && canReport(v) ? <ReportChip tone={tone} v={v} onReport={onReport} /> : null}
      </div>
      {queued.length ? (
        <p data-testid="scan-queued" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '12px 0 0', fontSize: 14, lineHeight: 1.5, color: t.ink2 }}>
          <Icon name="queued" size={18} color={t.warn} style={{ marginTop: 1 }} />
          <span>Walk-through video received. Processing hasn’t started yet. The 3D scan appears here when it’s ready.</span>
        </p>
      ) : null}
    </>
  );
}

function VisitRow({ t, tone, v, phases, view, onView, measuring, setMeasuring, sampleElevation, loadMapPreview, onSaveNote, onReport, onSaveMeasure, onRemoveMeasure }) {
  const status = visitStatus(v);
  const phase = v.phase_id ? v.phase_name || ((phases || []).find((p) => p.id === v.phase_id) || {}).name || 'Phase' : v.phase_name;
  const sub = [(v.source && v.source.label) || CAPTURE_LABEL[v.capture] || 'Capture visit', phase].filter(Boolean).join(' · ');
  const md = v.deliverables.find((x) => x.id && x.id === measuring && x.measure);
  const media = useMemo(() => galleryItems(v), [v]);
  const [gallery, setGallery] = useState(null);
  return (
    <li data-testid="site-visit" style={{ borderTop: `1px solid ${t.hair}`, padding: '24px 0' }}>
      <div className="sp-row">
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 500, color: t.ink }}>{humanDate(v.date) || 'Date to be set'}</div>
          <div style={{ fontSize: 13, color: t.mute, marginTop: 4 }}>{sub}</div>
        </div>
        <div className="sp-body">
          {v.deliverables.length ? (
            <DeliverableChips t={t} tone={tone} v={v} view={view} onView={onView} measuring={measuring} setMeasuring={setMeasuring}
              media={media} gallery={gallery} onGallery={setGallery} onReport={onReport} />
          ) : (
            <>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: t.mute }}>
                {v.status === 'failed' ? 'This visit wasn’t dispatched.' : 'Files appear here after the visit is processed.'}
              </p>
              {onReport && canReport(v) ? <div style={{ marginTop: 12 }}><ReportChip tone={tone} v={v} onReport={onReport} /></div> : null}
            </>
          )}
          <ProgressNote note={v.progress_note} canAdd={v.status !== 'failed'}
            tone={{ ink: t.ink, muted: t.mute, line: t.hairS, surface: t.surf, solidText: t.solidText, solid: t.solid }}
            onSave={onSaveNote ? (text) => onSaveNote(v.id, text) : undefined} />
        </div>
        <div style={{ justifySelf: 'end' }}><StatusPill status={status} tone={tone} /></div>
        {gallery && media.length ? (
          <div className="sp-full">
            <SiteGallery items={media} date={v.date} tone={tone} start={gallery.start} key={gallery.n} onClose={() => setGallery(null)} />
          </div>
        ) : null}
        {md ? (
          <div className="sp-full">
            <MeasureView key={md.id} deliverable={md} sampleElevation={sampleElevation} loadMapPreview={loadMapPreview} onClose={() => setMeasuring(null)} tone={tone} onSaveMeasure={onSaveMeasure} onRemoveMeasure={onRemoveMeasure} />
          </div>
        ) : null}
      </div>
    </li>
  );
}

// onSaveNote(visitId, text) -> Promise: given on the builder timeline only;
// share links render notes read-only. tone: 'light' (app) or 'dark' (share page).
// project ({ name, address, city }) heads the printable site report.
export default function SiteTimeline({ timeline, phases, project, sampleElevation, loadMapPreview, onSaveNote, onSaveMeasure, onRemoveMeasure, tone = 'light', showSummary = true }) {
  const t = theme(tone);
  const [report, setReport] = useState(null);
  const [measuring, setMeasuring] = useState(null);
  const [view, setView] = useState(null);
  const stageRef = useRef(null);
  const phone = useIsPhone();
  const kinds3d = (timeline && timeline.compare_3d_kinds) || [];
  const visits = (timeline && timeline.visits) || [];
  const kinds = (timeline && timeline.compare_kinds) || [];
  const line = showSummary ? headerLine(timeline && timeline.summary) : '';

  const onView = (item) => {
    setView({ mode: 'single', item });
    const el = stageRef.current;
    if (el && el.scrollIntoView) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: phone ? 'start' : 'center' }), 0);
  };

  if (!visits.length) {
    return (
      <div data-testid="site-timeline" style={{ color: t.ink }}>
        <div data-testid="site-empty" style={{ border: `1px solid ${t.hair}`, borderRadius: 2, padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 }}>
          <Icon name="site" size={32} color={t.mute} />
          <div style={{ fontSize: 17, fontWeight: 500 }}>No visits yet</div>
          <p style={{ margin: 0, fontSize: 14, color: t.mute, maxWidth: 360, lineHeight: 1.5 }}>Booked visits and their files appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="site-timeline" style={{ color: t.ink, fontFamily: F.display }}>
      <style>{CSS}</style>
      {line ? <p style={{ fontSize: 14, color: t.mute, margin: '0 0 24px' }}>{line}</p> : null}
      <SiteViewer t={t} tone={tone} visits={visits} kinds3d={kinds3d} view={view} setView={setView} stageRef={stageRef} />
      {kinds.length ? <ImageCompare t={t} tone={tone} visits={visits} kinds={kinds} /> : null}
      <section data-testid="site-visits">
        <SectionHead t={t} title="Visits" sub={`${visits.length} visit${visits.length === 1 ? '' : 's'}, newest first`} />
        {[timeline.deliverables_error, timeline.notes_error].filter(Boolean).map((msg) => (
          <p key={msg} role="alert" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '0 0 16px', padding: '12px 16px', fontSize: 14, lineHeight: 1.5,
            color: t.ink, background: t.badTint, borderLeft: `2px solid ${t.bad}` }}>
            <Icon name="alert" size={18} color={t.bad} style={{ marginTop: 1 }} />{msg}
          </p>
        ))}
        <ol style={{ listStyle: 'none', padding: 0, margin: 0, borderBottom: `1px solid ${t.hair}` }}>
          {visits.map((v) => (
            <VisitRow key={v.id} t={t} tone={tone} v={v} phases={phases} view={view} onView={onView}
              measuring={measuring} setMeasuring={setMeasuring} sampleElevation={sampleElevation} loadMapPreview={loadMapPreview} onSaveNote={onSaveNote} onReport={setReport} onSaveMeasure={onSaveMeasure} onRemoveMeasure={onRemoveMeasure} />
          ))}
        </ol>
      </section>
      {report ? <SiteReport timeline={timeline} visitId={report} project={project} phases={phases} onClose={() => setReport(null)} /> : null}
    </div>
  );
}
